import dayjs from 'dayjs';
import { PredefinedColors } from '@ionic/core';
import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import {
    KEYS_WITH_MEMBERS,
    ICareMap,
    IContactMethod,
    IHouseholdMember,
    INewCareMap,
    IPatient,
    ISupportGroup,
    ISupportGroupKey,
    ISupportGroupMember,
    SUPPORT_GROUP_KEYS,
    IChecklist,
    ICaremapAccess,
    IFamilyMember,
    IContactMethodType,
    IWizardAnswer,
} from './ICareMap';
import { CareMapFactory } from './CareMapFactory';
import { FlowDownloader } from './FlowDownloader';
import { MAX_HOUSEHOLD_MEMBERS } from '../nodes/NodeAdapter';
import { ILabelKey, LabelProvider } from '../config/LabelProvider';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { AppPrincipal } from '../auth/AppPrincipal';
import { AsyncStorageProvider, IAsyncSmartStorageProvider } from '../support/AsyncStorageProvider';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { WelcomePageState } from '../QuestionnaireWelcomePage';
import { AlertPresenter } from '../AlertBinder';
import { EnvConfiguration } from '../config/EnvConfiguration';
import { IOrganizationUser } from '../organizations/IOrganization';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { WithLinks } from './WithLinks';
import { SupportGroupKeySorter } from './SupportGroupKeySorter';
import { MAX_HONEYCOMB_SIZE } from '../nodes/HoneycombConfigProvider';
import { CompositeAnonymousHttpClient } from '../auth/anonymous/CompositeAnonymousHttpClient';
import { EmailValidator } from '../utils/EmailValidator';
import { UserState } from '../user/UserState';
import { Logger } from '../support/Logger';
import { isAutomatedTests } from '../utils/isAutomatedTests';
import { CareMapMerger } from './CareMapMerger';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';

// At some point, we likely will want userIsPatient to be a more first class
// property on caremap... but for now, it is stored in the exiting
//   initialWizardAnswers['recipient'] as if they choose Myself
// I did this because it was expedient (making use of infrastructure already in place)
//
// It is encapsulated by CareMapState.isUserThePatient(): boolean
//
// and the const that follows encapsulates what the value should be

const CAREMAP_MEDIA_TYPE = 'application/vnd.innerhive.caremap.v3+json';

export const MYSELF = { value: 'myself', label: 'Myself' };

export interface ISupportGroupLabels {
    title: string;
    memberTitle: string;
    shortMemberTitle: string;
    memberNameLabel: string;
    roleLabel: string;
    rolePlaceholder: string;
};

type ISupportGroupMembersMap = {[key in ISupportGroupKey]: ISupportGroupMember[] | undefined};

export class CareMapState extends State<Record<string, never>> {
    static inject = () => [
        APIClient,
        CareMapFactory,
        FlowDownloader,
        LabelProvider,
        MixpanelService,
        AppPrincipal,
        AsyncStorageProvider,
        BufferedEventBus,
        HistoryViewModel,
        WelcomePageState,
        AlertPresenter,
        EnvConfiguration,
        SupportGroupKeySorter,
        CompositeAnonymousHttpClient,
        UserState,
        Logger,
        CareMapMerger
    ];

    constructor(
        private apiClient: APIClient,
        private factory: CareMapFactory,
        private flowDownloader: FlowDownloader,
        private labelProvider: LabelProvider,
        private tracking: MixpanelService,
        private principal: AppPrincipal,
        private storageProvider: AsyncStorageProvider,
        private events: BufferedEventBus,
        private history: HistoryViewModel,
        private welcomePageState: WelcomePageState,
        private alert: AlertPresenter,
        private envConfig: EnvConfiguration,
        private keySorter: SupportGroupKeySorter,
        private anonymousHttpClient: CompositeAnonymousHttpClient,
        private userState: UserState,
        private logger: Logger,
        private merger: CareMapMerger
    ) {
        super({});

        // if the caremap is for the user, then set up labels
        // to use the MY_CAREMAP__ prefix when possible
        // so we can thing like ""...your caremap" instead of "...Lance's caremap"
        this.subscribe(() => {
            if (this.isUserThePatient() !== this.labelProvider.use_MY_CAREMAP__PrefixIfApplicable){
                this.labelProvider.use_MY_CAREMAP__PrefixIfApplicable = this.isUserThePatient();
            }
        });

        this.events.on('UserState.deleted', () => {
            this.storageProvider.getStringProvider('current-caremap-id').remove();
        });

        this.events.on('web-sockets.message', this.onWebsocketMessage);

        this.lastActiveCareMapIdStorage = this.storageProvider.getStringProvider('last-active-caremap-id');
        this.profileBackfillStorage = this.storageProvider.getStringProvider('profile-backfill-done');
    }

    private lastActiveCareMapIdStorage: IAsyncSmartStorageProvider<string>;
    private lastActiveCareMapId?: string;

    private pendingServerRefresh = false;

    private onWebsocketMessage = async (message?: IResourceChangedSocketMessage) => {
        const selfHref = this._activeCaremap?.links?.self?.href;
        if (!selfHref || !message || message.resourceToRefresh.toLowerCase() !== selfHref.toLowerCase()) {
            return;
        }

        if (this.isSaved()) {
            await this.fetchAndMergeServerState();
        } else {
            this.pendingServerRefresh = true;
        }
    };

    private fetchAndMergeServerState = async () => {
        try {
            const server = await this.apiClient.get(this._activeCaremap!.links.self, CAREMAP_MEDIA_TYPE);
            const merged = this.merger.merge({
                base: this.lastApiResponse,
                local: this._activeCaremap! as ICareMap,
                server
            });
            this.lastApiResponse = server;
            this._activeCaremap = merged;
            this.lastPatchedState = undefined; // force re-evaluation on next save
            this.careMaps = this.careMaps.map(map => server.careMapId === map.careMapId ? merged : map);
            this.setState({});
        } catch (err: any) {
            this.logger.error(`CareMapState.fetchAndMergeServerState failed: ${err.toString()}`);
        }
    };

    private maybeRefreshFromServer = () => {
        if (this.pendingServerRefresh) {
            this.pendingServerRefresh = false;
            this.fetchAndMergeServerState();
        }
    };

    // injected at IOC due to circular reference
    _orgState!: OrganizationsState;

    private _activeCaremap?: ICareMap | INewCareMap;

    // hold all care maps and expose helper methods
    private careMaps: ICareMap[] = [];
    private profileBackfillStorage: IAsyncSmartStorageProvider<string>;
    getCareMaps = (): ICareMap[] => {
        return this.careMaps;
    };

    getCareMapById = (careMapId: string) => {
        return this.careMaps.find(map => map.careMapId === careMapId);
    };

    // NOTE: doesn't apply to Organizations
    getIncompleteCareMap = (): ICareMap | undefined => {
        return this.careMaps.find(
            map => !this._orgState.isOrgTeamMember() && !map.essentialMapComplete
                && !map.patient.firstName && !map.patient.lastName // hasn't gone past the Care Recipient Name step in the mini-wizard
        );
    };

    // returns true if there is a caremap other than the current one that is MYSELF
    hasOtherCareMapForSelf = (): boolean => {
        const selfMap = this.careMaps.find(map => map.userId === this.principal.userId && map.initialWizardAnswers['recipient']?.value === MYSELF.value);
        return Boolean(selfMap && selfMap.careMapId !== this._activeCaremap?.careMapId);
    };

    hasAnyCareMapForSelf = (): boolean => {
        const selfMap = this.careMaps.find(map => map.userId === this.principal.userId && map.initialWizardAnswers['recipient']?.value === MYSELF.value);
        return Boolean(selfMap);
    };

    hasCareMaps = (): boolean => {
        return this.getCareMaps().length > 0;
    };

    private _ensureCareMapPromise?: Promise<void>;

    ensureCareMapExists = () => {
        if (this.hasCareMaps()) {
            this._ensureCareMapPromise = undefined;
            return Promise.resolve();
        }
        if (!this._ensureCareMapPromise) {
            this._ensureCareMapPromise = this.addNewCaremapV3({
                patient: {},
                isRecipientSelf: false,
                isAutoCreated: true
            }).then(async ({ careMapId }) => {
                await this.setActiveCaremap(careMapId);
            }).finally(() => {
                this._ensureCareMapPromise = undefined;
            });
        }
        return this._ensureCareMapPromise;
    };

    private getCareMapFromOrg = async (careMapId: string) => {
        const orgCaremapLite = this._orgState.getOrganizationCareMap(careMapId);
        if (orgCaremapLite?.links.self) {
            return await this.apiClient.get(orgCaremapLite?.links.self, CAREMAP_MEDIA_TYPE);
        } else {
            return undefined;
        }
    };

    getOrLoadCareMap = async (careMapId: string) => {
        let map: ICareMap | INewCareMap | undefined = this.getCareMapById(careMapId);
        if (!map){
            map = await this.getCareMapFromOrg(careMapId);
        }
        if (!map) {
            const error: any = Error(`Care Map with ID ${careMapId} not found (or don't have access)`);
            error.errorCode = 'not-found';
            throw error;
        }
        return map;
    };

    setActiveCaremap = async (careMapId: string) => {
        // if it is already active then do nothing
        // because otherwise we might stomp on changes in thereAdd commentMore actions
        // eg delete support group is waiting for its changes to save while redirecting
        if (this._activeCaremap?.careMapId === careMapId){
            return;
        }

        // WARNING: this block is identical to getOrLoadCareMap() above, but invoking it here inserts
        // an await into the main flow, creating a race condition that breaks the onboarding wizard
        let map: ICareMap | INewCareMap | undefined = this.getCareMapById(careMapId);
        if (!map){
            map = await this.getCareMapFromOrg(careMapId);
        }
        if (!map) {
            const error: any = Error(`Care Map with ID ${careMapId} not found (or don't have access)`);
            error.errorCode = 'not-found';
            throw error;
        }

        this._activeCaremap = map;
        if (map.careMapId) {
            this.lastApiResponse = JSON.parse(JSON.stringify(map)) as ICareMap;
        }
        this.setState({ });

        this.storageProvider.getStringProvider('current-caremap-id').set(careMapId);

        this.lastActiveCareMapId = careMapId;
        this.lastActiveCareMapIdStorage.set(careMapId);

        // lazy sync: update user's own household member record from profile if stale
        if (this.isUserProfileStaleOnCaremap()) {
            this.syncUserProfileToHouseholdMember();
        }

        // reverse sync: backfill empty profile fields from caremap member data
        this.backfillProfileFromHouseholdMember();
    };

    private isUserProfileStaleOnCaremap = (): boolean => {
        if (!this._activeCaremap) {
            return false;
        }
        if (!this.hasPermissionTo('edit')) {
            return false;
        }

        const email = this.userState.state.email;
        if (!email) {
            return false;
        }

        const member = this.getHouseholdMemberByUserEmail(email);
        if (!member) {
            return false;
        }

        const userFirstName = this.userState.state.firstName;
        const userLastName = this.userState.state.lastName;
        const userAddress = this.userState.state.address;

        if (userFirstName && member.firstName !== userFirstName) {
            return true;
        }
        if (userLastName && member.lastName !== userLastName) {
            return true;
        }
        if (userAddress && member.address !== userAddress) {
            return true;
        }

        return false;
    };

    syncUserProfileToHouseholdMember = () => {
        if (!this._activeCaremap) {
            return;
        }
        if (!this.hasPermissionTo('edit')) {
            return;
        }

        const email = this.userState.state.email;
        if (!email) {
            if (!isAutomatedTests()) {
                this.logger.error('syncUserProfileToHouseholdMember: authenticated user has no email');
            }
            return;
        }

        const member = this.getHouseholdMemberByUserEmail(email);
        if (!member) {
            return;
        }

        const contactMethods: IContactMethod[] = [...member.contactMethods];

        if (this.userState.state.phone) {
            const p = this.userState.state.phone;
            const formatted = /^\d{10}$/.test(p) ? `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}` : p;
            const mIdx = contactMethods.findIndex(m => m.type === 'mobile-phone');
            const mPhone: IContactMethod = { type: 'mobile-phone', value: formatted };
            if (mIdx > -1) {
                contactMethods.splice(mIdx, 1, mPhone);
            } else {
                contactMethods.push(mPhone);
            }
        }

        this.updateHouseholdMember({
            ...member,
            firstName: this.userState.state.firstName || member.firstName,
            lastName: this.userState.state.lastName || member.lastName,
            address: this.userState.state.address || member.address,
            contactMethods
        });
    };

    private backfillProfileFromHouseholdMember = async () => {
        if (await this.profileBackfillStorage.get()) {
            return;
        }

        if (!this._activeCaremap) {
            this.logger.error('backfillProfileFromHouseholdMember: no active caremap');
            return;
        }

        const email = this.userState.state.email;
        if (!email) {
            if (!isAutomatedTests()) {
                this.logger.error('backfillProfileFromHouseholdMember: authenticated user has no email');
            }
            return;
        }

        const member = this.getHouseholdMemberByUserEmail(email);
        if (!member) {
            return;
        }

        let backfilled = false;

        if (!this.userState.state.address && member.address) {
            this.userState.setAddress(member.address);
            backfilled = true;
        }

        if (!this.userState.state.phone) {
            const mobilePhone = member.contactMethods.find(m => m.type === 'mobile-phone');
            if (mobilePhone?.value) {
                const digits = mobilePhone.value.replace(/\D/g, '');
                this.userState.setPhone(digits);
                backfilled = true;
            }
        }

        // Set the flag only after guards pass, so transient failures can retry
        await this.profileBackfillStorage.set('true');

        if (backfilled) {
            // fire and forget, same pattern as other lazy syncs
            this.userState.save();
        }
    };

    unsetCurrentCareMap = async () => {
        this._activeCaremap = undefined;
        this.setState({ });
        await this.storageProvider.getStringProvider('current-caremap-id').remove();
    };

    getMostRecentCaremap = (): ICareMap | undefined => {
        if (this.getActiveCaremap()){
            return this.getActiveCaremap() as ICareMap;
        }
        if (this.lastActiveCareMapId && this.getCareMapById(this.lastActiveCareMapId)){
            return this.getCareMapById(this.lastActiveCareMapId);
        }
        return this.getCareMaps()[0];
    };

    hasActiveCareMap = () => {
        return Boolean(this.getActiveCaremap());
    };

    getActiveCaremap = (): ICareMap | INewCareMap| undefined => {
        return this._activeCaremap;
    };

    hasCompletedCareMap = () => {
        return this.getCareMaps().some(map => map.essentialMapComplete === true);
    };

    getLabel = (labelName: ILabelKey, placeholders: any = {}) => {
        return this.labelProvider.getLabel(labelName, { careRecipient: this.getPatientFirstName(), ...placeholders });
    };

    hasStartedWizard = () => Boolean(this._activeCaremap?.careMapId);

    deleteAndRestart = async () => {
        if (this._activeCaremap?.links){
            await this.apiClient.delete(this._activeCaremap.links.self);
            window.location.hash = '';
            window.location.reload();
        }
    };

    loadWithoutActive = async () => {
        if (this.principal.tokenType === 'caremap-viewer'){
            if (this.careMaps.length > 0){
                // the implementation below `loadUsingAnonymousSessions`
                // is using a cache from app start, and so this won't refresh
                // it is reasonably we might call this expecting it to refresh
                // I confirmed that we don't currently in the anonymous UX
                // but put this DX guard in place so that if we do, we are driven to here
                // at that point we basically want
                // 1. on first call, use the cache (to avoid fetching them all again)
                // 2. on subsequents calls, just refresh each using their self links, and
                //      CompositeAnonymousHttpClient will use the correct link... BUT this might be
                //      when we discover the JWT for that caremap has expired
                throw new Error('calling CareMapState.loadWithoutActive more than once is not yet supported for an anonymous session');
            }
            await this.loadUsingAnonymousSessions();
        } else {
            const entry = await this.apiClient.entry();
            this.careMaps = await this.apiClient.get(entry.links.careMaps, CAREMAP_MEDIA_TYPE);
            this.lastActiveCareMapId = await this.lastActiveCareMapIdStorage.get();
            this.setState({});

            this.profileTracking();
        }
    };

    private profileTracking = () => {
        this.events.emit('MixPanelService.care-recipient-relationships', this.getUserBelongingRelationships());

        // 'Care Recipient Profiles'
        this.tracking.setProfileProp({prop: 'Care Recipient Profiles', val: Number(this.hasAnyCareMapForSelf())});

        // 'Caregiver Profiles'
        this.tracking.setProfileProp({prop: 'Caregiver Profiles', val: this.caregiverProfileCount()});
    };

    private getMeAsMembers = () => {
        let members: IHouseholdMember[] = [];
        this.careMaps.forEach(map => {
            members = members.concat(map.household.members.filter(m => m.userEmail === this.userState.state.email));
        });
        return members;
    };

    private caregiverProfileCount = () => {
        let numMaps = 0;
        this.getMeAsMembers().forEach(member => {
            numMaps += member.primaryCaregiver ? 1 : 0;
        });
        return numMaps;
    };

    private getUserBelongingRelationships = () => {
        return this.getMeAsMembers().map(member => member.relationship);
    };

    private loadUsingAnonymousSessions = async () => {
        this.careMaps = this.anonymousHttpClient.getInitCaremaps();
        this.setState({ });
    };

    private adaptCareMapForApi = (careMap: ICareMap | INewCareMap): ICareMap => {
        // remove photoDataURI b/c we never want to persist that to the API
        // set notes to empty array because those aren't saved by PUT Caremap

        const copy: ICareMap = JSON.parse(JSON.stringify(careMap));
        delete copy.patient.photoDataURI;

        for (const section of KEYS_WITH_MEMBERS) {

            for (const member of copy[section].members){
                if (section === 'household') {
                    delete (member as IHouseholdMember).photoDataURI;
                }

            }
        }
        return copy;
    };

    private savingPromise?: Promise<void>;
    private isSaveEnqueued = false;
    private is409Retrying = false;

    isSaved = () => !this.savingPromise && !this.isSaveEnqueued;

    enqueueSave = (): void => {
        // if there is a save in flight, then enqueue for when they are all done
        if (this.savingPromise){
            this.isSaveEnqueued = true;
        }
        // otherwise, do it now
        else {
            this.dequeueSave();
        }
    };

    enqueuedSaveError?: any;

    private setEnqueuedSaveError = (err: any) => {
        this.enqueuedSaveError = err;

        // emit so consumers know to check enqueuedSaveError
        this.setState({});
    };

    clearEnqueuedSaveError = () => {
        if (this.enqueuedSaveError){
            this.enqueuedSaveError = undefined;

            // emit so consumers know to check enqueuedSaveError
            this.setState({ });
        }
    };

    private dequeueSave = () => {
        // NOTE: maybe we shouldn't try and save while there is an error
        // or maybe the latest state would clear the error...
        // currently we still let it save b/c likely it will result in the same error
        // which does little harm, but it might clear the error

        this.isSaveEnqueued = false;

        // NOTE: important that we sync start a save, so that this.savingPromise gets set
        this.save().then(() => {
            this.clearEnqueuedSaveError();
            this.maybeRefreshFromServer();
        }).catch(err => {
            this.setEnqueuedSaveError(err);
            this.maybeRefreshFromServer();
        });
    };

    private lastPatchedState?: string = undefined;

    private save = async (): Promise<void> => {
        // if we already saving, then queue it up for when that one is done
        if (this.savingPromise){
            return this.savingPromise
                .catch(err => {
                    // ignore an error on the previous save, it is
                    // that callers responsibility to handle that failure
                })
                .then(() => this.save());
        }

        this.savingPromise = Promise.resolve().then(async () => {
            try {
                const careMap = this.getActiveCaremap();
                if (!careMap){
                    // TODO: we should still save, but we no longer have the memory reference to save
                    return;
                }
                if (!careMap.careMapId) {
                    // this creates a copy of the body, so when we save this.lastPatchedState it reflects what we actually sent
                    const finalBody = this.adaptCareMapForApi(careMap);
                    const headers = {
                        accept: CAREMAP_MEDIA_TYPE,
                        'content-type': CAREMAP_MEDIA_TYPE
                    };

                    // API errors out otherwise
                    // we're only temporarily using to get the appropriate POST link
                    delete finalBody.links;

                    const entry = await this.apiClient.entry();
                    const result = await this.apiClient.post(
                        entry.links.careMaps,
                        finalBody,
                        headers
                    );

                    this.lastApiResponse = result;
                    this.lastPatchedState = JSON.stringify({ ...finalBody, version: undefined });

                    // add the new care map to our collection
                    this.careMaps.push(result);
                    this.setActiveCaremap(result.careMapId);
                } else {
                    const body = {
                        ...careMap,
                    };

                    // this creates a copy of the body, so when we save this.lastPatchedState it reflects what we actually sent
                    const finalBody = this.adaptCareMapForApi(body);

                    // don't save if nothing changed
                    if (this.lastPatchedState !== JSON.stringify({ ...finalBody, version: undefined })){
                        const result = await this.apiClient.put(
                            careMap,
                            finalBody,
                            {
                                accept: CAREMAP_MEDIA_TYPE,
                                'content-type': CAREMAP_MEDIA_TYPE
                            }
                        );

                        const merged = this.merger.merge({
                            base: this.lastApiResponse,
                            local: this._activeCaremap! as ICareMap,
                            server: result
                        });
                        this.lastApiResponse = result;
                        this._activeCaremap = merged;
                        this.lastPatchedState = JSON.stringify({ ...finalBody, version: undefined });
                        this.careMaps = this.careMaps.map(map => result.careMapId === map.careMapId ? merged : map);
                        this.setState({ });
                    }
                }
            } catch (e: any) {
                if (e.status === 409) {
                    if (this.is409Retrying) {
                        this.is409Retrying = false;
                        e.displayMessage = 'Sorry, this care map has already been updated. Please refresh and try again.';
                        throw e;
                    }

                    try {
                        const server = await this.apiClient.get(this._activeCaremap!.links.self, CAREMAP_MEDIA_TYPE);
                        const merged = this.merger.merge({
                            base: this.lastApiResponse,
                            local: this._activeCaremap! as ICareMap,
                            server
                        });
                        this.lastApiResponse = server;
                        this._activeCaremap = merged;
                        this.pendingServerRefresh = false;
                        this.lastPatchedState = undefined; // force re-save
                        this.careMaps = this.careMaps.map(map => server.careMapId === map.careMapId ? merged : map);
                        this.setState({});

                        // Clear savingPromise so save() can re-enter, then retry once
                        this.savingPromise = undefined;
                        this.is409Retrying = true;
                        return this.save().finally(() => {
                            this.is409Retrying = false;
                        });
                    } catch (recoveryError: any) {
                        this.is409Retrying = false;
                        recoveryError.displayMessage = 'Sorry, this care map has already been updated. Please refresh and try again.';
                        throw recoveryError;
                    }
                }

                if (e.status === 404 && e.errorCode === 'caremap.deleted') {
                    const name = this.getPatientFirstName();
                    await this.alert.showAlertV2({
                        header: 'Care Map Deleted',
                        message: `Sorry... ${name ? `${name}'s` : 'This'} care map has been deleted in a different session, so you can no longer make changes.`
                    });

                    await this.afterDelete();
                    return;
                }

                throw e;
            }
        });

        return this.savingPromise.finally(() => {
            this.savingPromise = undefined;

            // if a save was enqueued, attempt to dequeue it
            if (this.isSaveEnqueued){
                this.dequeueSave();
            }
        });
    };

    delete = async () => {
        await this.apiClient.delete(this._activeCaremap?.links.self);

        const self = this._activeCaremap?.initialWizardAnswers['recipient-self'];
        this.tracking.track('Care Map Deleted', () => ({
            'Care Map State': this._activeCaremap?.essentialMapComplete ? 'completed' : 'questionnaire',
            'Care Recipient': self ? 'myself' : self === false ? 'someone else' : undefined
        }));

        // can only have one care map that is 'self'
        if (self) {
            // 'Care Recipient Profiles': 0
            this.events.emit('MixPanelService.set-profile-prop', {
                prop: 'Care Recipient Profiles',
                val: 0
            });
        }

        await this.afterDelete();
    };

    private afterDelete = async () => {
        // delete the care map in this.careMaps and remove id from storage
        this.careMaps = this.careMaps.filter(map => this._activeCaremap?.careMapId !== map.careMapId);
        await this.storageProvider.getStringProvider('current-caremap-id').remove();

        if (!this.hasCareMaps()) {
            this.welcomePageState.setState({hasContinued: false});
        }

        this.history.replace('/');
    };

    getHouseholdMembers = (): IHouseholdMember[] => {
        return this._activeCaremap!.household.members;
    };

    updateHouseholdMembers = (members: IHouseholdMember[]) => {
        this._activeCaremap = {
            ...this._activeCaremap!,
            household: {
                ...this._activeCaremap!.household,
                members: members.map(member => ({
                    ...member,
                    contactMethods: this.cleanupContactMethods(member.contactMethods)
                })),
            }
        };
        this.setState({ });
        this.enqueueSave();
    };

    addHouseholdMembers = (newMembers: IHouseholdMember[]) => {
        try {
            if (this._activeCaremap!.household.members.length + newMembers.length > MAX_HOUSEHOLD_MEMBERS) {
                this.tracking.track('Tried to Add Too Many Members', () => ({
                    category: 'household'
                }));
                const error: any = new Error(`cannot add more than ${MAX_HOUSEHOLD_MEMBERS} inner circle members`);
                error.displayMessage = `Sorry, currently you cannot add more than ${MAX_HOUSEHOLD_MEMBERS} inner circle members.`;
                throw error;
            }

            // only add userEmail if it doesn't exist, and email is valid
            // it may exist (and already have been 'validated') like in OnboardCareRecipientWizard
            for (const member of newMembers) {
                const email = member.contactMethods.find(m=>m.type === 'email');
                if (!member.userEmail && email && email.value && EmailValidator.test(email.value)) {
                    member.userEmail = email.value;
                }

                if (member.primaryCaregiver && member.userEmail === this.userState.state.email) {
                    // 'Caregiver Profiles'
                    this.tracking.incrementProfileProp({prop: 'Caregiver Profiles', by: 1});
                }
            }

            const members = [
                ...this._activeCaremap!.household.members,
                ...newMembers.map(member => ({
                    ...member,
                    contactMethods: this.cleanupContactMethods(member.contactMethods)
                }))
            ];

            this._activeCaremap = {
                ...this._activeCaremap!,
                household: {
                    ...this._activeCaremap!.household,
                    members,
                }
            };
            this.setState({ });
            this.enqueueSave();
        } catch (err) {
            // caller assumes enqueueSave and therefore isn/t expecting to deal with errors directly
            this.setEnqueuedSaveError(err);
        }
    };

    removeHouseholdMember = (memberId: string) => {
        console.log('REMOVE', memberId);
        const member = this._activeCaremap!.household.members.find(m => m.id === memberId);
        const members = this._activeCaremap!.household.members.filter(m => m.id !== memberId);
        this._activeCaremap = {
            ...this._activeCaremap!,
            household: {
                ...this._activeCaremap!.household,
                members
            }
        };
        this.setState({ });
        this.enqueueSave();

        if (member?.primaryCaregiver && member.userEmail === this.userState.state.email) {
            // 'Caregiver Profiles'
            this.tracking.incrementProfileProp({prop: 'Caregiver Profiles', by: -1});
        }
    };

    // NOTE: if marking the map complete is needed in other updates, consider moving complete logic to enqueueSave?
    updatePatient = (patient: IPatient, options?: { markEssentialMapComplete: boolean; initialWizardAnswers: IWizardAnswer[] }) => {
        const payload = {
            ...this._activeCaremap!,
            patient: {
                ...patient,
                contactMethods: this.cleanupContactMethods(patient.contactMethods)
            }
        };

        if (options?.markEssentialMapComplete) {
            payload.essentialMapComplete = true;
        }

        if (options?.initialWizardAnswers) {
            let initialWizardAnswers = this._activeCaremap!.initialWizardAnswers || {};
            for (const answer of options.initialWizardAnswers) {
                initialWizardAnswers = {
                    ...initialWizardAnswers,
                    [answer.questionId]: answer.answer
                };
            }

            payload.initialWizardAnswers = initialWizardAnswers;
        }

        this._activeCaremap = payload;

        this.setState({ });
        this.enqueueSave();
    };

    updateHouseholdMember = (member: IHouseholdMember) => {
        console.log('UPDATE', member);
        const idx = this._activeCaremap!.household.members.findIndex(item => item.id === member.id);

        this._activeCaremap!.household.members[idx] = {
            ...member,
            contactMethods: this.cleanupContactMethods(member.contactMethods)
        };

        this._activeCaremap = {
            ...this._activeCaremap!,
            household: {
                ...this._activeCaremap!.household,
                members: this._activeCaremap!.household.members
            }
        };

        this.setState({ });
        this.enqueueSave();
    };

    getHouseholdMember = (memberId: string): IHouseholdMember => {
        const member = this.maybeGetHouseholdMember(memberId);
        if (!member) {
            // this really should never happen
            throw new Error(`Member with memberId ${memberId} not found`);
        }
        return member;
    };

    getHouseholdMemberByUserEmail = (email: string): IHouseholdMember | undefined => {
        const member = this.getCareMap().household.members.find(m => m.userEmail === email);
        return member;
    };

    maybeGetHouseholdMember = (memberId: string): IHouseholdMember | undefined => {
        return this.getCareMap().household.members.find(m => m.id === memberId);
    };

    answerInitialWizardQuestion = ({ questionId, answer }: IWizardAnswer) => {
        const initialWizardAnswers = this._activeCaremap!.initialWizardAnswers || {};

        this._activeCaremap = {
            ...this._activeCaremap!,
            initialWizardAnswers: {
                ...initialWizardAnswers,
                [questionId]: answer
            }
        };

        this.setState({ });
        this.enqueueSave();
    };

    getInitialWizardAnswer = (questionId: string): any => {
        const initialWizardAnswers = this._activeCaremap?.initialWizardAnswers || {};

        return initialWizardAnswers[questionId];
    };

    isCommunityExtracurricularRelevant = () => {
        return Boolean(this._activeCaremap?.initialWizardAnswers['community']);
    };

    isCommunityAdvocacyRelevant = () => {
        return Boolean(this._activeCaremap?.initialWizardAnswers['community2']);
    };

    addChecklists = (supportGroupKey: ISupportGroupKey, checklists: {templateId: string, html: string}[]) => {
        // all or nothing. if there are ANY duplicates, no checklists are added
        for (const checklist of checklists) {
            if (checklist.templateId && this._activeCaremap![supportGroupKey].checklists.some((c: IChecklist) => c.templateId === checklist.templateId)){
                throw new Error(`a checklist for templateId ${checklist.templateId} already exists for Care Map ${this._activeCaremap!.careMapId}. You must remove it in order to add this template again.`);
                // why? b/c duplicating a checklist is almost certainly not what is wanted and merging a newer version
                // of the checklist with an older version of the checklist is out of scope (for the foreseeable future)
            }
        }

        for (const checklist of checklists) {
            this._activeCaremap![supportGroupKey].checklists.push(checklist);
        }

        this.enqueueSave();
    };

    getSupportGroup = (supportGroupKey: ISupportGroupKey) => {
        return this._activeCaremap![supportGroupKey];
    };

    addOrUpdateSupportGroup = ({ supportGroupKey, members = [] }: { supportGroupKey: ISupportGroupKey; members?: ISupportGroupMember[] }) => {
        const isAdd = !this._activeCaremap![supportGroupKey].isRelevant;

        const supportGroup = {
            ...this._activeCaremap![supportGroupKey]!,
            members,
            isRelevant: true
        };
        if (isAdd){
            supportGroup.isRelevantSince = new Date().toISOString();
        }
        this.setSupportGroupState(supportGroupKey, supportGroup);

        this.enqueueSave();

        if (isAdd) {
            this.events.emit('CareMapState.SupportGroupAdded', supportGroupKey);
        }
    };

    removeSupportGroup = ({ supportGroupKey }: { supportGroupKey: ISupportGroupKey }) => {
        if (this._activeCaremap![supportGroupKey].members.length > 0){
            throw new Error(`cannot remove ${supportGroupKey} it has members ${JSON.stringify(this._activeCaremap![supportGroupKey].members)}`);
        }
        this.setSupportGroupState(supportGroupKey, {
            // we aren't yet explicitly deleting their notes, in part because
            // we don't want to require them to delete their notes in order to get the delete button
            // like we do members.
            // By not actually deleting notes, they are recoverable when they add the group
            // and easy for the user to then delete them if they don't want them
            ...this._activeCaremap![supportGroupKey],

            // remove checklists
            checklists: [],

            // this is what actually causes it to delete / not show up anymore
            isRelevant: false,
            isRelevantSince: undefined
        });
        this.enqueueSave();
    };

    shouldShowNameFirst = (supportGroupKey: ISupportGroupKey) => {
        return (['community', 'financial'].includes(supportGroupKey));
    };

    getChecklists = (supportGroupKey: ISupportGroupKey): IChecklist[] => {
        const checkLists = this.getCareMap()[supportGroupKey].checklists;
        return checkLists || [];
    };

    getAllSupportGroupLabels = (): {[key in ISupportGroupKey]: ISupportGroupLabels} => {
        const labels: {[key in ISupportGroupKey]: ISupportGroupLabels} = {
            'community': {
                title: 'Community',
                memberTitle: 'Community Support',
                shortMemberTitle: 'Support',
                memberNameLabel: 'Support Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__COMMUNITY__ROLE'),
                rolePlaceholder: 'Search or select support type'
            },
            'financial': {
                title: 'Financial & Legal',
                memberTitle: 'Support',
                shortMemberTitle: 'Support',
                memberNameLabel: this.labelProvider.getLabel('SUPPORT_GROUP__FINANCIAL__ROLE'),
                roleLabel: 'Support Type',
                rolePlaceholder: 'Search or select support type'
            },
            'school': {
                title: 'School',
                memberTitle: 'School Team Member',
                shortMemberTitle: 'Member',
                memberNameLabel: 'Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__SCHOOL__ROLE'),
                rolePlaceholder: 'Search or select member'
            },
            'medical': {
                title: 'Primary Care',
                memberTitle: 'Care Team Member',
                shortMemberTitle: 'Member',
                memberNameLabel: 'Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__MEDICAL__ROLE'),
                rolePlaceholder: 'Search or select member'
            },
            'specialists': {
                title: 'Specialists',
                memberTitle: 'Specialist',
                shortMemberTitle: 'Specialist',
                memberNameLabel: 'Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__SPECIALISTS__ROLE'),
                rolePlaceholder: 'Search or select specialist'
            },
            'social': {
                title: 'Living',
                memberTitle: 'Support',
                shortMemberTitle: 'Support',
                memberNameLabel: 'Name',
                roleLabel: this.labelProvider.getLabel('SUPPORT_GROUP__SOCIAL__ROLE'),
                rolePlaceholder: 'Search or select support type'
            }
        };

        return labels;
    };

    getSupportGroupLabels = (supportGroupKey: ISupportGroupKey): ISupportGroupLabels => {
        return this.getAllSupportGroupLabels()[supportGroupKey];
    };

    getSupportGroupColor = (supportGroupKey: ISupportGroupKey): PredefinedColors => {
        const keys = this.keySorter.getSortedSupportGroupKeys(this.getActiveCaremap());
        const index = keys.indexOf(supportGroupKey);
        return index%2 ? 'primary' : 'secondary';
    };

    canDeleteSupportGroup = (supportGroupKey: ISupportGroupKey): boolean => {
        return this._activeCaremap?.links.self.actions.includes('delete-support-group')
            && this._activeCaremap[supportGroupKey].members.length === 0;
    };

    hasPermissionTo = (...operations:string[]) => {
        const allowed = this._activeCaremap?.links?.self?.actions || [];
        return operations.every(o => allowed.includes(o));
    };

    getNewSupportGroupMember = ({supportGroupKey, roleId}: {supportGroupKey: ISupportGroupKey, roleId?: string}): ISupportGroupMember => {
        return this.factory.createSupportGroupMember(supportGroupKey, roleId);
    };

    cleanupContactMethods = (contactMethods: IContactMethod[]): IContactMethod[] => {
        // shouldn't have to coalesce to empty array, but wizard tests were failing without doing this
        return (contactMethods || []).filter(method => method.value);
    };

    getContactMethodOrDefault = (node: IFamilyMember, type: IContactMethodType) => {
        return node.contactMethods.find(m=>m.type === type) || {type: type, value: ''};
    };

    addSupportGroupMember = (supportGroupKey: ISupportGroupKey, member: ISupportGroupMember) => {
        // caller assumes enqueueSave and therefore isn/t expecting to deal with errors directly
        Promise.resolve().then(() => {
            const maxHoneycombHexagons = MAX_HONEYCOMB_SIZE;

            if (this._activeCaremap![supportGroupKey].members.length === maxHoneycombHexagons) {
                this.tracking.track('Tried to Add Too Many Members', () => ({
                    category: supportGroupKey
                }));
                const error: any = new Error(`cannot add more than ${maxHoneycombHexagons} members to the ${supportGroupKey} support group`);
                error.displayMessage = `Sorry, currently you cannot add more than ${maxHoneycombHexagons} supports to ${this.getSupportGroupLabels(supportGroupKey).title}`;
                throw error;
            }

            const members = [
                ...this._activeCaremap![supportGroupKey].members,
                {
                    ...member,
                    contactMethods: this.cleanupContactMethods(member.contactMethods)
                }
            ];

            if (!this.findSupportGroupMember({ supportGroupKey, memberId: member.id })){
                this.addOrUpdateSupportGroup({ supportGroupKey, members });
            }
        }).catch(err => {
            this.setEnqueuedSaveError(err);
        });
    };

    private setSupportGroupState = (supportGroupKey: ISupportGroupKey, state: ISupportGroup) => {
        this._activeCaremap = {
            ...this._activeCaremap!,
            [supportGroupKey]: state
        };
        this.setState({ });
    };

    findSupportGroupMember = ({supportGroupKey, memberId}: {supportGroupKey: ISupportGroupKey, memberId: string}): ISupportGroupMember | undefined => {
        const members = this._activeCaremap![supportGroupKey].members.filter(m => m.id === memberId);
        if (members.length > 1){
            throw new Error(`found ${members.length} with memberId ${memberId}`);
        }
        return members[0];
    };

    findSupportGroupMemberByRole = ({supportGroupKey, roleId}: {supportGroupKey: ISupportGroupKey, roleId: string | undefined}): ISupportGroupMember | undefined => {
        const members = this._activeCaremap ? this._activeCaremap[supportGroupKey].members.filter(m => m.roleId === roleId) : [];
        if (members.length > 1){
            console.warn(`found ${members.length} with role ${roleId}`);
        }
        return members[0];
    };

    updateSupportGroupMember = (supportGroupKey: ISupportGroupKey, member: ISupportGroupMember) => {
        const members = this._activeCaremap![supportGroupKey].members;
        const idx = members.findIndex(item => item.id === member.id);

        members[idx] = {
            ...member,
            contactMethods: this.cleanupContactMethods(member.contactMethods)
        };

        this.setSupportGroupState(supportGroupKey, {
            ...this._activeCaremap![supportGroupKey],
            members,
        });
        this.enqueueSave();
    };

    private getIsRelevantWhenRemovingMemberWhileInWizard = ({ group, supportGroupKey }: {group: ISupportGroup; supportGroupKey: ISupportGroupKey; }): boolean => {
        if (group.members.length > 0){
            return true;
        }

        // if no members but answered yes to one of many is relevant questions
        if (supportGroupKey === 'community') {
            return this.isCommunityAdvocacyRelevant() || this.isCommunityExtracurricularRelevant();
        }

        // otherwise, removed last member, no longer relevant
        return false;
    };

    removeSupportGroupMember = ({ supportGroupKey, memberId }: {supportGroupKey: ISupportGroupKey; memberId: string; }) => {
        const group: ISupportGroup = this._activeCaremap![supportGroupKey];

        const updatedGroup: ISupportGroup = {
            ...group,
            members: group.members.filter(m => m.id !== memberId),
        };

        if (!this._activeCaremap?.essentialMapComplete){
            updatedGroup.isRelevant = this.getIsRelevantWhenRemovingMemberWhileInWizard({
                group: updatedGroup,
                supportGroupKey
            });
        }

        if (!updatedGroup.isRelevant) {
            updatedGroup.checklists = [];
        }

        this.setSupportGroupState(supportGroupKey, updatedGroup);

        this.enqueueSave();
    };

    getSupportGroupMembers = (supportGroupKey: ISupportGroupKey): ISupportGroupMember[] => {
        const supportGroup = this._activeCaremap?.[supportGroupKey];
        return supportGroup?.members || [];
    };

    // get all the members in all support groups from the care map
    getAllSupportGroupsMembers = (): ISupportGroupMember[] => {
        let members: ISupportGroupMember[] = [];

        SUPPORT_GROUP_KEYS.forEach(key => {
            const supportGroup = this._activeCaremap?.[key];
            if (supportGroup?.members) {
                members = members.concat(supportGroup.members);
            }
        });

        return members;
    };

    // get all the members in all support groups from the care map, as a map / json object
    getAllSupportGroupsMembersAsMap = (): Partial<ISupportGroupMembersMap> => {
        const members: Partial<ISupportGroupMembersMap> = {};

        SUPPORT_GROUP_KEYS.forEach(key => {
            const supportGroup = this._activeCaremap?.[key];
            if (supportGroup?.members) {
                members[key] = supportGroup.members;
            }
        });

        return members;
    };

    getSupportGroupMember = ({ supportGroupKey, memberId }: { supportGroupKey?: ISupportGroupKey, memberId: string }) => {
        let members: ISupportGroupMember[];

        if (supportGroupKey) {
            const supportGroup = this.getCareMap()[supportGroupKey];
            members = supportGroup?.members || [];
        } else {
            members = this.getAllSupportGroupsMembers();
        }

        return members.find(m => m.id === memberId);
    };

    findSupportGroupMemberCategory = (memberId: string | undefined): ISupportGroupKey | undefined => {
        let categoryId: ISupportGroupKey | undefined = undefined;
        const members = this.getAllSupportGroupsMembersAsMap();

        Object.keys(members).forEach(key => {
            if (members[key as ISupportGroupKey]?.find(member => member.id === memberId)) {
                categoryId = key as ISupportGroupKey;
            };
        });

        return categoryId;
    };

    getSupportGroupMemberInformation = (memberId: string | undefined) => {
        const member = this.getSupportGroupMember({memberId: memberId || ''});
        const categoryId = this.findSupportGroupMemberCategory(member?.id);
        const showNameFirst = this.shouldShowNameFirst(categoryId as ISupportGroupKey);
        return {member, categoryId, showNameFirst};
    };

    setPatientFirstName = (firstName: string) => {
        this._activeCaremap = {
            ...this._activeCaremap!,
            patient: { ...this._activeCaremap!.patient, firstName }
        };
        this.setState({ });
        this.enqueueSave();
    };

    isUserThePatient = (): boolean => {
        const isPatientTheOwner = this._activeCaremap?.initialWizardAnswers['recipient']?.value === MYSELF.value;

        // if the caremap doesn't have a userId yet (because it is in the process of being created)
        // then this user owns it
        const ownerUserId = (this._activeCaremap as ICareMap | undefined)?.userId || this.principal.userId;

        const isUserTheOwner = ownerUserId  === this.principal.userId;

        return isPatientTheOwner && isUserTheOwner;
    };

    getPatientFirstName = (careMap?: ICareMap): string => {
        return (careMap ? careMap.patient.firstName : this._activeCaremap ? this._activeCaremap.patient.firstName : '') || '';
    };

    setPatientLastName = (lastName: string) => {
        this._activeCaremap = {
            ...this._activeCaremap!,
            patient: { ...this._activeCaremap!.patient, lastName }
        };
        this.setState({ });
        this.enqueueSave();
    };

    getPatientLastName = (careMap?: ICareMap): string => {
        return (careMap ? careMap.patient.lastName : this._activeCaremap ? this._activeCaremap.patient.lastName : '') || '';
    };

    getPatientFullName = (careMap?: ICareMap): string => {
        const first = this.getPatientFirstName(careMap);
        const last = this.getPatientLastName(careMap);
        return first + (first && last ? ' ' : '') + last;
    };

    setPatientDOB = (dob: string) => {
        const dayjsDob = dayjs(dob);

        if (!dayjsDob.isValid() && dob !== '') {
            throw new Error('Invalid date for patient DOB');
        }

        this._activeCaremap = {
            ...this._activeCaremap!,
            patient: { ...this._activeCaremap!.patient, dob }
        };
        this.setState({});
        this.enqueueSave();
    };

    getPatientDOB = (): string => {
        return this._activeCaremap!.patient.dob || '';
    };

    uploadFile = async (file: File) => {
        const entry = await this.apiClient.entry();
        const { uploadUrl, cdnUrl, headers } = await this.apiClient.post(entry.links.uploadsV2, { fileName: file.name });

        const res = await window.fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers,
        });
        if (res.status !== 200){
            throw new Error(`Upload failed with status ${res.status}`);
        }

        return { url: cdnUrl };
    };

    download = async () => {
        const filename = `${this.getPatientFullName()} Care Map ${dayjs().format('YYYY-MM-DD')}.png`;

        await this.flowDownloader.downloadFlowAsPNG({ flowName: 'full', filename });
    };

    createViewerAccessToken = async (days: number): Promise<{ access_token: string; session_token: string; }> => {
        let expiresISO8601 = dayjs().add(days, 'days').toISOString();
        if (days === .0007){
            expiresISO8601 = dayjs().add(1, 'minute').toISOString();
        }
        return await this.apiClient.post(this._activeCaremap!.links.createViewerAccessToken, {
            expiresISO8601
        });
    };

    getInviteLink = async ({ inviteeName, inviteeEmail, access, patientName }: { inviteeName: string; inviteeEmail: string; access: ICaremapAccess; patientName: string; }) => {
        const { token } = await this.apiClient.post(this._activeCaremap!.links.invites, {
            name: inviteeName,
            email: inviteeEmail,
            access
        });
        return `${this.envConfig['WEB_BASE_URL']}/#/accept-invite?invite=${encodeURIComponent(token)}&access=${access}&care-recipient=${encodeURIComponent(patientName)}`;
    };

    // internal use
    copy = {
        out: () => console.log(JSON.stringify({
            ...this.adaptCareMapForApi(this._activeCaremap!),
            careMapId: undefined,
            userId: undefined,
            links: undefined,
            version: undefined
        })),
        in: async (incomingCareMap: any) => {
            if (!incomingCareMap){
                throw new Error('incomingCareMap required');
            }
            const { careMapId, links, version } = (this._activeCaremap as any) || {};

            this._activeCaremap = { ...incomingCareMap, careMapId, links, version };
            this.setState({ });
            await this.save();

            window.location.reload();
        }
    };

    // internal use
    duplicateCareMap = async () => {
        const duplicate = {
            ...this.adaptCareMapForApi(this._activeCaremap!),
            careMapId: undefined,
            version: undefined,
            links: undefined
        };
        this._activeCaremap = duplicate;

        this.setState({});
        await this.save();
        window.location.reload();
    };

    private lastApiResponse?: ICareMap;

    getCareMap = (): ICareMap => {
        return this._activeCaremap! as ICareMap;
    };

    addCaremapToClientUser = async (user: WithLinks<IOrganizationUser>) => {
        const headers = {
            accept: CAREMAP_MEDIA_TYPE,
            'content-type': CAREMAP_MEDIA_TYPE
        };
        const body = this.factory.createBlank();
        body.initialWizardAnswers['recipient']  = {value: 'myself', label: 'Myself'};
        body.initialWizardAnswers['recipient-self'] = true;
        body.initialWizardAnswers['skip-steps'] = [
            'my-name', 'name',
            'recipient-self', 'recipient'
        ];

        body.patient.firstName = user.firstName;
        body.patient.lastName = user.lastName;

        const result = await this.apiClient.post(user.links.caremaps, body, headers);
        this.careMaps = [
            ...(this.careMaps || []), result
        ];

        return { careMapId: result.careMapId };
    };

    addNewCaremapV2 = async () => {
        const headers = {
            accept: CAREMAP_MEDIA_TYPE,
            'content-type': CAREMAP_MEDIA_TYPE
        };
        const body = this.factory.createBlank();

        const entry = await this.apiClient.entry();

        const result = await this.apiClient.post(entry.links.careMaps, body, headers);
        this.careMaps = [
            ...(this.careMaps || []), result
        ];

        return { careMapId: result.careMapId };
    };

    addNewCaremapV3 = async ({
        patient,
        isRecipientSelf,
        householdMembers = [],
        isAutoCreated,
    }: {
        patient: Partial<IPatient>;
        isRecipientSelf: boolean;
        householdMembers?: IHouseholdMember[];
        isAutoCreated?: boolean;
    }) => {
        const headers = {
            accept: CAREMAP_MEDIA_TYPE,
            'content-type': CAREMAP_MEDIA_TYPE
        };
        const body = this.factory.createBlank(true);

        body.patient = {
            ...body.patient,
            ...patient
        };

        body.household.members = [...body.household.members, ...householdMembers];

        body.initialWizardAnswers = {
            // there is some old coupling to this that isn't worth addressing at this time
            // so just adapting to look the way we expect
            'recipient': isRecipientSelf ? MYSELF : undefined,
            'recipient-self': isRecipientSelf,
            [isRecipientSelf ? 'consent-self-date' : 'consent-someone-else-date'] : new Date().toISOString(),
            'onboarding/add-care-recipient/started': new Date().toISOString()
        };

        body.essentialMapComplete = isAutoCreated ? false : true;

        const entry = await this.apiClient.entry();

        const result = await this.apiClient.post(entry.links.careMaps, body, headers);
        this.careMaps = [
            ...(this.careMaps || []), result
        ];

        // can only have one care map that is 'self'
        if (isRecipientSelf) {
            this.events.emit('MixPanelService.set-profile-prop', {
                prop: 'Care Recipient Profiles',
                val: 1
            });
        }

        return { careMapId: result.careMapId };
    };

    removeMe = async () => {
        const activeCareMap = this.getActiveCaremap();

        if(!activeCareMap){
            throw new Error('expected activeCareMap');
        }

        if (!activeCareMap.links.removeSelf){
            throw new Error('expected activeCareMap.links.removeSelf');
        }

        await this.apiClient.delete(activeCareMap.links.removeSelf);

        // home so it can decide where to go (maybe org, maybe caregiver)
        this.history.push('/');

        this.careMaps = this.careMaps.filter(c => c.careMapId !== activeCareMap.careMapId);
        this.lastActiveCareMapId = undefined;
        await this.lastActiveCareMapIdStorage.remove();
        this.unsetCurrentCareMap();
    };

    removeOrgClient = async (user: IOrganizationUser) => {
        const userCaremaps = this.careMaps.filter(c => c.userId !== user.userId);
        const userCaremapIds = userCaremaps.map(c => c.careMapId);

        const caremaps = this.careMaps.filter(c => c.userId !== user.userId);
        if (caremaps.length < this.careMaps.length){
            this.careMaps = caremaps;
        }

        if (userCaremapIds.includes(this.lastActiveCareMapId || '')){
            this.lastActiveCareMapId = undefined;
            await this.lastActiveCareMapIdStorage.remove();
        }

        if (userCaremapIds.includes(this._activeCaremap?.careMapId || '')){
            await this.unsetCurrentCareMap();
        }

        this.setState({ });
    };

    hasStartedOnboardingStep = (step: ICareMapOnboardingStep) => {
        return Boolean(this.getInitialWizardAnswer(`onboarding/${step}/started`));
    };

    hasCompletedOnboardingStep = (step: ICareMapOnboardingStep) => {
        return Boolean(this.getInitialWizardAnswer(`onboarding/${step}/completed`)) || false;
    };

    isInOnboardingStep = (step: ICareMapOnboardingStep) => {
        return this.hasStartedOnboardingStep(step) && !this.hasCompletedOnboardingStep(step);
    };

    startOnboardingStep = (step: ICareMapOnboardingStep) => {
        this.answerInitialWizardQuestion({
            questionId: `onboarding/${step}/started`,
            answer: new Date().toISOString()
        });
    };

    completeOnboardingStep = (step: ICareMapOnboardingStep) => {
        this.answerInitialWizardQuestion({
            questionId: `onboarding/${step}/completed`,
            answer: new Date().toISOString()
        });
    };

    isPatientIncomplete = (options: { excludeDOB?: boolean } = {}) => {
        const patient = this.getActiveCaremap()!.patient;
        return (
            !patient.diagnoses?.length &&
            !patient.allergies?.length &&
            !patient.contactMethods.length &&
            !patient.address &&
            (options?.excludeDOB || !patient.dob)
        );
    };

};

export type ICareMapOnboardingStep =
    // this is where they get a chance to add an avatar
    // and in the future (add caregiver to household)
    'add-care-recipient' | 'household-members' | 'add-note' |

    // NOTE: these can be dynamically built like in Checklists.tsx
    'started-getting-started' | 'started-estate-planning' | 'started-advance-care';
